// src/components/InKindLanding.js
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import inkind_header from "../../images/inkind-header.png";

const InKindLanding = () => {
    ReactGA.send({
        hitType: "pageview",
        page: window.location.pathname,
        title: "المساهمات العينية - محصول",
    });

    // Get user info from localStorage
    const userType = localStorage.getItem("type");
    const userCoopType = localStorage.getItem("cooperativeType");

    // Determine user permissions
    const isConsumerCooperative = userType === "cooperative" && userCoopType === "consumer";
    const isServiceProvider = userType === "service_provider";
    const canDonateCrops = !isServiceProvider && !isConsumerCooperative; // All users can donate crops
    const canDonateInputs = false; // All users can donate inputs
    const canDonateServices = isServiceProvider; // Only service providers can donate services
    const canRequestHelp = isConsumerCooperative; // Only consumer cooperatives can request help

    // Check if user can do anything at all
    const userCanDonate = canDonateCrops || canDonateInputs || canDonateServices;
    const userCanRequest = canRequestHelp;
    const userHasAnyAccess = userCanDonate || userCanRequest;

    // Create contribution types dynamically based on user permissions
    const getContributionTypes = () => {
        const types = [];

        // Only include crops if user can donate OR request
        if (canDonateCrops || canRequestHelp) {
            types.push({
                id: 1,
                title: "المحاصيل",
                description: "جاهز لتقديم حصة مساهمة ثبات",
                description_need: "طلب المحاصيل الزراعية في مساهمة ثبات",
                icon: "🌾",
                donateLink: "/home/donate-crop",
                requestLink: "/home/request-crop-help",
                donateText: "تقديم مساهمة بمحصول",
                requestText: "طلب الحصة من المحاصيل",
                canDonate: canDonateCrops,
                canRequest: canRequestHelp,
                donatePermission: "جميع المستخدمين",
                requestPermission: "الجمعيات التعاونية الاستهلاكية فقط",
                color: "from-green-500 to-emerald-600",
                bgColor: "bg-green-50",
                borderColor: "border-green-200"
            });
        }

        // // Only include inputs if user can donate OR request
        // if (canDonateInputs || canRequestHelp) {
        //     types.push({
        //         id: 2,
        //         title: "المدخلات الزراعية",
        //         description: "تقديم أو طلب البذور، الأسمدة، والمبيدات",
        //         icon: "🌱",
        //         donateLink: "/home/donate-input",
        //         requestLink: "/home/request-input-help",
        //         donateText: "تقديم مساهمة بمدخلات",
        //         requestText: "طلب مدخلات",
        //         canDonate: canDonateInputs,
        //         canRequest: canRequestHelp,
        //         donatePermission: "جميع المستخدمين",
        //         requestPermission: "الجمعيات التعاونية الاستهلاكية فقط",
        //         color: "from-blue-500 to-cyan-600",
        //         bgColor: "bg-blue-50",
        //         borderColor: "border-blue-200"
        //     });
        // }

        // Only include services if user can donate OR request
        if (canDonateServices || canRequestHelp) {
            types.push({
                id: 3,
                title: "الخدمات الزراعية",
                description: "جاهز لتقديم حصة مساهمة ثبات",
                description_need: "طلب الخدمات الزراعية في مساهمة ثبات",
                icon: "🚜",
                donateLink: "/home/donate-service",
                requestLink: "/home/request-service-help",
                donateText: "تقديم مساهمة بخدمة",
                requestText: "طلب خدمة",
                canDonate: canDonateServices,
                canRequest: canRequestHelp,
                donatePermission: "مقدمو الخدمات فقط",
                requestPermission: "الجمعيات التعاونية الاستهلاكية فقط",
                color: "from-orange-500 to-amber-600",
                bgColor: "bg-orange-50",
                borderColor: "border-orange-200"
            });
        }

        return types;
    };

    const contributionTypes = getContributionTypes();
    const stats = [
        { number: "500+", label: "مساهمة عينية" },
        { number: "300+", label: "طلب الحصة" },
        { number: "200+", label: "مزارع مستفيد" },
        { number: "50+", label: "تعاونية مشاركة" }
    ];

    // User type display
    const getUserTypeDisplay = () => {
        if (userType === "cooperative") {
            return userCoopType === "consumer" ? "جمعية تعاونية استهلاكية" : "جمعية تعاونية زراعية";
        }
        if (userType === "service_provider") return "مقدم خدمات";
        if (userType === "farmer") return "مزارع";
        if (userType === "supplier") return "مورد";
        return "مستخدم";
    };

    // If user has no access at all
    if (!userHasAnyAccess) {
        return (
            <div className="bg-gray-50 w-full flex items-center justify-center" dir="rtl">
                <div className="max-w-4xl mx-auto px-4 py-12 text-center">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12">
                        <div className="text-6xl mb-6">🔒</div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-4">
                            غير مصرح لك بالوصول
                        </h1>
                        <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                            حسابك الحالي لا يسمح لك تقديم مساهمة أو طلب حصة المساهمة في الوقت الحالي.
                            يرجى التواصل مع إدارة النظام إذا كنت تعتقد أن هذا خطأ.
                        </p>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-2xl mx-auto">
                            <h3 className="font-bold text-yellow-800 mb-2">أنواع الحسابات المسموح لها:</h3>
                            <ul className="text-yellow-700 text-right space-y-2">
                                <li>• المزارعون والموردون: يمكنهم تقديم مساهمة بالمحاصيل </li>
                                <li>• مقدمو الخدمات: يمكنهم تقديم مساهمة بالخدمات</li>
                                <li>• الجمعيات الاستهلاكية: يمكنهم طلب الحصة</li>
                                <li>• الجمعيات الزراعية: يمكنهم تقديم مساهمة بالمحاصيل </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 w-full" dir="rtl">
            <Helmet>
                <title> مساهمة ثبات </title>
                <meta name="description" content="ساهم في تثديم المساهمة للمزارعين و التعانيات الاسنخلاكية من خلال تقديم مساهمة بالمحاصيل أو الخدمات. أو اطلب حصة المساهمة إذا كنت بحاجة لحصة المساهمة." />
                <meta property="og:title" content="مساهمة ثبات" />
                <meta property="og:description" content="منصة تقديم مساهمة وطلب حصة المساهمة العينية في القطاع الزراعي" />
            </Helmet>

            {/* User Info Banner */}
            <div className="bg-white border-b border-gray-200 py-4">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between">
                        <div className="text-right">
                            <h2 className="text-lg font-semibold text-gray-800">
                                مرحباً بك في منصة تيراب
                            </h2>
                            <p className="text-sm text-gray-600">
                                نوع حسابك: <span className="font-medium text-primary">{getUserTypeDisplay()}</span>
                            </p>
                        </div>
                        <div className="bg-primary text-white px-3 py-1 rounded-full text-sm">
                            {isConsumerCooperative ? "يمكنك طلب الحصة" :
                                isServiceProvider ? "يمكنك تقديم مساهمة بالخدمات" :
                                    "يمكنك تقديم مساهمة بالمحاصيل"}
                        </div>
                    </div>
                </div>
            </div>

            {/* Hero Section */}
            <div className="relative min-h-[400px] bg-gradient-to-br from-green-600 to-emerald-700 text-white py-24"
                style={{ backgroundImage: `url(${inkind_header})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="absolute inset-0 bg-black opacity-30"></div>
                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        مساهمة ثبات
                    </h1>
                    <p className="text-xl md:text-2xl mb-8 opacity-90">
                        يداً بيد لبناء مجتمع زراعي أقوى
                    </p>
                    <p className="text-lg max-w-3xl mx-auto leading-relaxed">
                        واجهة لتقديم مساهمة أو طلب حصة المساهمة العينية في القطاع الزراعي. ساهم بفائض محاصيلك أو مدخلاتك،
                        أو اطلب الحصة إذا كنت بحاجة لحصة المساهمة.
                    </p>
                </div>
            </div>

            {/* Stats Section */}
            {/* <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {stats.map((stat, index) => (
                        <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                            <div className="text-2xl md:text-3xl font-bold text-green-600 mb-2">
                                {stat.number}
                            </div>
                            <div className="text-gray-600 font-medium">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div> */}

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-4">
                        كيف تريد المساهمة؟
                    </h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        اختر نوع المساهمة التي تناسبك سواء كنت تريد تقديم مساهمة أو طلب الحصة
                    </p>
                </div>

                {/* If user can only donate or only request, show simplified view */}
                {(!userCanDonate || !userCanRequest) ? (
                    <div className="mb-12">
                        <div className={`text-center mb-8 ${userCanDonate ? 'text-green-600' : 'text-blue-600'
                            }`}>
                            <div className="text-5xl mb-4">
                                {userCanDonate ? '🎁' : '🙏'}
                            </div>
                            <h3 className="text-2xl font-bold mb-2">
                                {userCanDonate
                                    ? 'اختر نوع المساهمة الذي ترغب فيه'
                                    : 'اختر المجال الذي تحتاج فيه لحصة المساهمة'}
                            </h3>
                        </div>
                    </div>
                ) : null}

                {/* Contribution Types Grid - Only show cards where user has at least one permission */}
                {contributionTypes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {contributionTypes.map((type) => (
                            <div
                                key={type.id}
                                className={`${type.bgColor} rounded-2xl shadow-lg border ${type.borderColor} overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2`}
                            >
                                {/* Header */}
                                <div className={`bg-gradient-to-r ${type.color} p-6 text-center`}>
                                    <div className="text-4xl mb-3">{type.icon}</div>
                                    <h3 className="text-xl font-bold">{type.title}</h3>
                                    {type.canDonate && (
                                        <p className="text-opacity-90 mt-2">{type.description}</p>
                                    )}
                                    {type.canRequest && (
                                        <p className="text-opacity-90 mt-2">{type.description_need}</p>
                                    )}
                                </div>

                                {/* Actions - Only show sections the user can access */}
                                <div className="p-6">
                                    {/* Show Donate section only if user can donate this type */}
                                    {type.canDonate && (
                                        <div className={type.canRequest ? "mb-6" : ""}>
                                            <div className="flex items-center mb-3">
                                                <div className="w-3 h-3 bg-green-500 rounded-full mx-2"></div>
                                                <h4 className="font-bold text-gray-800">أريد تقديم مساهمة</h4>
                                            </div>
                                            <p className="text-gray-600 text-sm mb-2">
                                                لديك فائض وتريد المساهمة 
                                            </p>
                                            <Link
                                                to={type.donateLink}
                                                className="block w-full bg-green-500 hover:bg-green-600 text-white text-center py-3 px-4 rounded-lg font-bold transition-colors duration-200"
                                                onClick={() => ReactGA.event({
                                                    category: 'In-Kind',
                                                    action: 'Click Donate',
                                                    label: type.title
                                                })}
                                            >
                                                {type.donateText}
                                            </Link>
                                        </div>
                                    )}

                                    {/* Show Request section only if user can request this type */}
                                    {type.canRequest && (
                                        <div>
                                            <div className="flex items-center mb-3">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full mx-2"></div>
                                                <h4 className="font-bold text-gray-800">جاهز لطلب الحصة</h4>
                                            </div>
                                            <p className="text-gray-600 text-sm mb-2">
                                               طلب الحصة من {type.title.toLowerCase()}
                                            </p>
                                            <Link
                                                to={type.requestLink}
                                                className="block w-full bg-blue-500 hover:bg-blue-600 text-white text-center py-3 px-4 rounded-lg font-bold transition-colors duration-200"
                                                onClick={() => ReactGA.event({
                                                    category: 'In-Kind',
                                                    action: 'Click Request',
                                                    label: type.title
                                                })}
                                            >
                                                {type.requestText}
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-6">😔</div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">
                            لا توجد خيارات متاحة
                        </h3>
                        <p className="text-gray-600">
                            لا توجد مساهمات متاحة لنوع حسابك الحالي.
                        </p>
                    </div>
                )}

                {/* My Contributions Management Card */}
                {userHasAnyAccess && (
                    <div className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl shadow-lg overflow-hidden mb-12">
                        <div className="p-8">
                            {/* Change justify-between to justify-start and reverse the order */}
                            <div dir="rtl" className="flex flex-col md:flex-row items-center md:justify-start gap-6">
                                {/* Text section - now on the right in RTL */}
                                <div className="mb-6 md:mb-0 md:ml-6 text-center md:text-right flex-1">
                                    <h3 className="text-2xl font-bold mb-2">إدارة مساهماتي</h3>
                                    <p className="text-opacity-90">
                                        تابع مساهماتك وطلبات مساهمة ثبات في مكان واحد
                                    </p>
                                </div>

                                {/* Buttons section - now on the left in RTL */}
                                <div dir="rtl" className="flex flex-col sm:flex-row-reverse gap-4 justify-end">
                                    {/* Show crops donations link for non-service providers */}
                                    {userCanDonate && !isServiceProvider && (
                                        <Link
                                            to="/home/profile/crops/my-donations"
                                            className="bg-white text-green-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Donations Button',
                                                label: 'Crops Donations'
                                            })}
                                        >
                                            <span className="ml-2">🎁</span>
                                            مساهماتي في المحاصيل
                                        </Link>
                                    )}

                                    {/* Show services donations link for service providers */}
                                    {isServiceProvider && (
                                        <Link
                                            to="/home/profile/services/my-donations"
                                            className="bg-white text-orange-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Donations Button',
                                                label: 'Services Donations'
                                            })}
                                        >
                                            <span className="ml-2">🚜</span>
                                            مساهماتي في الخدمات
                                        </Link>
                                    )}

                                    {/* Show crops needs link for consumer cooperatives */}
                                    {isConsumerCooperative && (
                                        <Link
                                            to="/home/profile/crops/my-needs"
                                            className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Needs Button',
                                                label: 'Crops Needs'
                                            })}
                                        >
                                            <span className="ml-2">🙏</span>
                                            طلباتي في المحاصيل
                                        </Link>
                                    )}

                                    {/* Show services needs link for consumer cooperatives */}
                                    {isConsumerCooperative && (
                                        <Link
                                            to="/home/profile/services/my-needs"
                                            className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Needs Button',
                                                label: 'Services Needs'
                                            })}
                                        >
                                            <span className="ml-2">🚜</span>
                                            طلباتي في الخدمات
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* User Type Info Section */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">معلومات عن أنواع الحسابات والصلاحيات</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className={`text-center p-4 rounded-lg border ${(userType === "farmer" || userType === "supplier")
                            ? "bg-green-100 border-green-300"
                            : "bg-green-50 border-green-200"
                            }`}>
                            <div className="text-2xl mb-2">🌾</div>
                            <h4 className="font-bold text-green-800 mb-2">المزارعون</h4>
                            <p className="text-sm text-green-700">يمكنهم تقديم مساهمة بالمحاصيل  الزراعية</p>
                        </div>
                        <div className={`text-center p-4 rounded-lg border ${userType === "service_provider"
                            ? "bg-blue-100 border-blue-300"
                            : "bg-blue-50 border-blue-200"
                            }`}>
                            <div className="text-2xl mb-2">🚜</div>
                            <h4 className="font-bold text-blue-800 mb-2"> الشركات و مقدمي الخدمات </h4>
                            <p className="text-sm text-blue-700">يمكنهم تقديم مساهمة بالخدمات الزراعية</p>
                        </div>
                        <div className={`text-center p-4 rounded-lg border ${isConsumerCooperative
                            ? "bg-purple-100 border-purple-300"
                            : "bg-purple-50 border-purple-200"
                            }`}>
                            <div className="text-2xl mb-2">👥</div>
                            <h4 className="font-bold text-purple-800 mb-2">الجمعيات الاستهلاكية</h4>
                            <p className="text-sm text-purple-700">يمكنهم طلب حصة المساهمة في جميع المجالات</p>
                        </div>
                        <div className={`text-center p-4 rounded-lg border ${userType === "cooperative" && userCoopType === "agriculture"
                            ? "bg-orange-100 border-orange-300"
                            : "bg-orange-50 border-orange-200"
                            }`}>
                            <div className="text-2xl mb-2">🏢</div>
                            <h4 className="font-bold text-orange-800 mb-2">الجمعيات الزراعية</h4>
                            <p className="text-sm text-orange-700">يمكنهم تقديم مساهمة بالمحاصيل </p>
                        </div>
                    </div>
                    {/* Highlight current user type */}
                    <div className="mt-8 p-4 bg-primary bg-opacity-10 border border-primary rounded-lg">
                        <p className="text-center text-primary font-bold">
                            أنت حالياً: <span className="text-lg">{getUserTypeDisplay()}</span>
                        </p>
                    </div>
                </div>

                {/* How It Works Section */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">كيف تعمل مساهمة ثبات؟</h3>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <div className="bg-green-100 text-green-600 rounded-full p-2 mx-3 mt-1">
                                        <span className="font-bold">1</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800">اختر نوع المساهمة</h4>
                                        <p className="text-gray-600 text-sm">

                                            حدد ما إذا كنت تريد تقديم مساهمة أو طلب الحصة حسب صلاحيات حسابك

                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-green-100 text-green-600 rounded-full p-2 mx-3 mt-1">
                                        <span className="font-bold">2</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800">املأ بيانات الطلب</h4>
                                        <p className="text-gray-600 text-sm">أدخل التفاصيل المطلوبة عن المحصول أو الخدمة أو المدخل</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-green-100 text-green-600 rounded-full p-2 mx-3 mt-1">
                                        <span className="font-bold">3</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800">التواصل والإتمام</h4>
                                        <p className="text-gray-600 text-sm">سيقوم فريق محصول بالتواصل معك لإتمام العملية</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <h4 className="font-bold text-gray-800 mb-4 text-lg">معلومات مهمة</h4>
                            <ul className="space-y-3 text-gray-600 text-sm">
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>جميع المساهمات مجانية تماماً</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>يتم المراجعة من قبل فريق محصول</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>نضمن وصول المساهمات لمستحقيها</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>بياناتك محمية وسرية</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>الصلاحيات محددة حسب نوع الحساب</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="text-center bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold mb-4">هل لديك استفسار؟</h3>
                    <p className="text-green-600 mb-6 max-w-2xl mx-auto">
                        فريق محصول جاهز لمساعدتك في أي استفسار حول مساهمة ثبات وكيفية المشاركة
                    </p>
                    <a href="tel:3623" className="bg-white text-green-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition-colors duration-200">
                        اتصل بنا
                    </a>
                </div>
            </div>
        </div>
    );
};

export default InKindLanding;